"use client"

import { CircleDot } from "lucide-react"

import { BAUSTEIN_BY_ID } from "@/modules/lernpfad/data/bausteine"
import { evidenceIndex } from "@/modules/lernpfad/engine"
import type { LernpfadDemoState } from "@/app/demo/lernpfad/use-lernpfad-demo"

/**
 * "Meine Kurse" — the transcript, from the same side the evidence actually
 * lives on. Each row shows the STRONGEST thing known about that completion —
 * which is the honest LTEM question made visible one row at a time: how much
 * of "completed" is a result, and how much is only attendance.
 */
export default function LmsTranscript({ state }: { state: LernpfadDemoState }) {
    const { employee, localizer } = state
    const { t } = localizer
    const evidence = evidenceIndex(employee.evidence)

    const rows = [...employee.completed].sort((a, b) => (a.completedOn < b.completedOn ? 1 : -1))

    if (rows.length === 0) {
        return <p className="p-6 text-center text-sm text-muted-foreground">{t("lms.transcript.empty")}</p>
    }

    return (
        <ul className="divide-y p-4 pt-3">
            {rows.map((completion) => {
                const baustein = BAUSTEIN_BY_ID.get(completion.bausteinId)
                if (!baustein) return null
                const content = localizer.baustein(baustein)
                const record = evidence.get(completion.bausteinId)

                return (
                    <li key={completion.bausteinId} className="flex items-center gap-3 py-3">
                        <CircleDot className="size-4 shrink-0 text-muted-foreground" />
                        <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium">{content.title}</p>
                            <p className="text-xs text-muted-foreground">
                                {t("lms.transcript.completedOn", { date: localizer.date(completion.completedOn) })}
                            </p>
                        </div>
                        <span className="shrink-0 rounded-full border bg-muted/30 px-2 py-0.5 text-xs text-muted-foreground">
                            {record?.learning
                                ? t("lms.transcript.evidence.learning", {
                                      instrument: t(`instrument.${record.learning.instrument}.short` as never),
                                      percent: Math.round(record.learning.scaled * 100),
                                  })
                                : record?.reaction
                                  ? t("lms.transcript.evidence.reaction")
                                  : t("lms.transcript.evidence.attendance")}
                        </span>
                    </li>
                )
            })}
        </ul>
    )
}
